import { useEffect, useRef } from 'react'

import { MAP_PATH, SECTOR_PATH, TICKETS_PATH } from '@/app/router/paths'
import {
  GTM_CURRENCY_CODE,
  pushEcommerceEvent,
} from '@/utils/analytics/ecommerceDataLayer'
import { pushOneSignalTracking } from '@/utils/analytics/oneSignalTracking'
import { useCurrentPath } from './useCurrentPath'
import { useEcommerceItems } from './useEcommerceItems'

export const useCartTracking = () => {
  const ecommerceItems = useEcommerceItems()
  const prevItems = useRef(ecommerceItems)
  const { currentPath } = useCurrentPath()

  useEffect(() => {
    if (![MAP_PATH, SECTOR_PATH, TICKETS_PATH].includes(currentPath)) return

    const prev = prevItems.current
    const curr = ecommerceItems

    const prevMap = new Map(prev?.map((item) => [item.item_id, item]))
    const currMap = new Map(curr?.map((item) => [item.item_id, item]))

    let hasAdded = false
    let hasRemoved = false
    let hasIncreasedQty = false
    let hasDecreasedQty = false

    for (const [id, currItem] of currMap.entries()) {
      const prevItem = prevMap.get(id)
      const currQuantity = currItem?.quantity ?? 0
      const prevQuantity = prevItem?.quantity ?? 0
      if (!prevItem) {
        hasAdded = true
      } else if (currQuantity > prevQuantity) {
        hasIncreasedQty = true
      } else if (currQuantity < prevQuantity) {
        hasDecreasedQty = true
      }
    }

    for (const id of prevMap.keys()) {
      if (!currMap.has(id)) {
        hasRemoved = true
      }
    }

    if (hasAdded || hasIncreasedQty) {
      pushEcommerceEvent('add_to_cart', {
        currency: GTM_CURRENCY_CODE,
        items: curr,
      })

      pushOneSignalTracking('add_to_cart', {
        ecommerce: {
          currency: GTM_CURRENCY_CODE,
          items: curr,
        },
      })
    }

    if (hasRemoved || hasDecreasedQty) {
      pushEcommerceEvent('remove_from_cart', {
        currency: GTM_CURRENCY_CODE,
        items: curr,
      })
    }

    prevItems.current = ecommerceItems
  }, [ecommerceItems, currentPath])
}
